import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { ProtectedRoute } from '@/components/shared/ProtectedRoute';
import { AdminRoute } from '@/components/shared/AdminRoute';
import { SuperAdminRoute } from '@/components/shared/SuperAdminRoute';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@elka/ui';
import { ThemeProvider } from '@elka/theme';
import { AppDataProvider } from '@/contexts/AppDataContext';

// Lazy load pages for better code splitting
const LoginForm = lazy(() =>
  import('@/features/auth/components/LoginForm').then((module) => ({
    default: module.LoginForm,
  }))
);
const ClientsPage = lazy(() =>
  import('@/pages/ClientsPage').then((module) => ({ default: module.ClientsPage }))
);
const ClientDetailPage = lazy(() =>
  import('@/pages/ClientDetailPage').then((module) => ({ default: module.ClientDetailPage }))
);
const ProjectsPage = lazy(() =>
  import('@/pages/ProjectsPage').then((module) => ({ default: module.ProjectsPage }))
);
const ProjectDetailPage = lazy(() =>
  import('@/pages/ProjectDetailPage').then((module) => ({ default: module.ProjectDetailPage }))
);
const DealOnboardingPage = lazy(() =>
  import('@/pages/DealOnboardingPage').then((module) => ({ default: module.DealOnboardingPage }))
);
const SuppliersPage = lazy(() =>
  import('@/pages/SuppliersPage').then((module) => ({ default: module.SuppliersPage }))
);
const SupplierDetailPage = lazy(() =>
  import('@/pages/SupplierDetailPage').then((module) => ({ default: module.SupplierDetailPage }))
);
const OrdersPage = lazy(() =>
  import('@/pages/OrdersPage').then((module) => ({ default: module.OrdersPage }))
);
const OrderNewPage = lazy(() =>
  import('@/pages/OrderNewPage').then((module) => ({ default: module.OrderNewPage }))
);
const SidurPage = lazy(() =>
  import('@/pages/SidurPage').then((module) => ({ default: module.SidurPage }))
);
const WorkersPage = lazy(() =>
  import('@/pages/WorkersPage').then((module) => ({ default: module.WorkersPage }))
);
const WorkerDetailPage = lazy(() =>
  import('@/pages/WorkerDetailPage').then((module) => ({ default: module.WorkerDetailPage }))
);
const GoodsReceivingPage = lazy(() =>
  import('@/pages/GoodsReceivingPage').then((module) => ({ default: module.GoodsReceivingPage }))
);
const PurchaseRequestsPage = lazy(() =>
  import('@/pages/PurchaseRequestsPage').then((module) => ({
    default: module.PurchaseRequestsPage,
  }))
);
const StockListPage = lazy(() =>
  import('@/features/inventory/stock/StockListPage').then((m) => ({ default: m.StockListPage }))
);
const ItemDetailPage = lazy(() =>
  import('@/features/inventory/stock/ItemDetailPage').then((m) => ({ default: m.ItemDetailPage }))
);
const MovementsPage = lazy(() =>
  import('@/features/inventory/stock/MovementsPage').then((m) => ({ default: m.MovementsPage }))
);
const WarehouseIncomingPage = lazy(() =>
  import('@/features/inventory/warehouse/WarehouseIncomingPage').then((m) => ({
    default: m.WarehouseIncomingPage,
  }))
);
const PrinterDiagnosticsPage = lazy(() =>
  import('@/features/inventory/labels/print/PrinterDiagnosticsPage').then((m) => ({
    default: m.PrinterDiagnosticsPage,
  }))
);
const ReturnsConfirmPage = lazy(() =>
  import('@/features/inventory/warehouse/ReturnsConfirmPage').then((m) => ({
    default: m.ReturnsConfirmPage,
  }))
);
const AdminAuditLogsPage = lazy(() =>
  import('@/pages/AdminAuditLogsPage').then((module) => ({ default: module.AdminAuditLogsPage }))
);
const GmailAuthCallbackPage = lazy(() =>
  import('@/pages/GmailAuthCallbackPage').then((module) => ({
    default: module.GmailAuthCallbackPage,
  }))
);
const AdminVacationRequestsPage = lazy(() =>
  import('@/pages/AdminVacationRequestsPage').then((module) => ({
    default: module.AdminVacationRequestsPage,
  }))
);
const AdminPettyCashPage = lazy(() =>
  import('@/pages/AdminPettyCashPage').then((module) => ({ default: module.AdminPettyCashPage }))
);
const AdminPayslipsPage = lazy(() =>
  import('@/pages/AdminPayslipsPage').then((module) => ({ default: module.AdminPayslipsPage }))
);
const AdminTimeReportsPage = lazy(() =>
  import('@/pages/AdminTimeReportsPage').then((module) => ({
    default: module.AdminTimeReportsPage,
  }))
);
const AdminDailyReportsPage = lazy(() =>
  import('@/pages/AdminDailyReportsPage').then((module) => ({
    default: module.AdminDailyReportsPage,
  }))
);
const AdminNotificationsPage = lazy(() =>
  import('@/pages/AdminNotificationsPage').then((module) => ({
    default: module.AdminNotificationsPage,
  }))
);
const AdminReminderSettingsPage = lazy(() =>
  import('@/pages/AdminReminderSettingsPage').then((module) => ({
    default: module.AdminReminderSettingsPage,
  }))
);
const AdminHolidaysPage = lazy(() =>
  import('@/pages/AdminHolidaysPage').then((module) => ({
    default: module.AdminHolidaysPage,
  }))
);
const SupplierBidsPage = lazy(() =>
  import('@/pages/SupplierBidsPage').then((module) => ({ default: module.SupplierBidsPage }))
);
const SupplierBidRequestDetailPage = lazy(() =>
  import('@/pages/SupplierBidRequestDetailPage').then((module) => ({
    default: module.SupplierBidRequestDetailPage,
  }))
);
const SupplierBidEntryPage = lazy(() =>
  import('@/pages/SupplierBidEntryPage').then((module) => ({
    default: module.SupplierBidEntryPage,
  }))
);
const SupplierQuotePage = lazy(() =>
  import('@/pages/SupplierQuotePage').then((module) => ({ default: module.SupplierQuotePage }))
);
const ToolboxPage = lazy(() =>
  import('@/pages/ToolboxPage').then((module) => ({ default: module.ToolboxPage }))
);
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((module) => ({ default: module.DashboardPage }))
);
const PublicDashboardPage = lazy(() =>
  import('@/pages/PublicDashboardPage').then((module) => ({
    default: module.PublicDashboardPage,
  }))
);
const WikiLayout = lazy(() =>
  import('@/features/wiki/components/WikiLayout').then((module) => ({
    default: module.WikiLayout,
  }))
);
const WikiPageView = lazy(() =>
  import('@/features/wiki/components/WikiPageView').then((module) => ({
    default: module.WikiPageView,
  }))
);

// Loading fallback component - smooth transition without layout shift
function PageLoadingFallback() {
  return (
    <div className="flex min-h-[400px] items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
}

function AppContent() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            {/* Public supplier quote page - no auth required */}
            <Route
              path="/supplier-quote/:token"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <SupplierQuotePage />
                </Suspense>
              }
            />

            {/* Public KPI dashboard — secret link + PIN, no Google auth (TV / external viewer) */}
            <Route
              path="/public-dashboard"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <PublicDashboardPage />
                </Suspense>
              }
            />

            {/* Login route */}
            <Route
              path="/login"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <LoginForm />
                </Suspense>
              }
            />

            {/* KPI command center — authed but chrome-less (no Layout sidebar/header) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <DashboardPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Wiki — authed, own sidebar (no Layout wrapper) */}
            <Route
              path="/wiki"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <WikiLayout />
                  </Suspense>
                </ProtectedRoute>
              }
            >
              <Route
                index
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <WikiPageView />
                  </Suspense>
                }
              />
              <Route
                path=":slug"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <WikiPageView />
                  </Suspense>
                }
              />
            </Route>

            <Route
              element={
                <ProtectedRoute>
                  <AppDataProvider>
                    <Layout />
                  </AppDataProvider>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/projects/clients" replace />} />

              {/* PROJECTS SECTION */}
              <Route
                path="/projects/clients"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <ClientsPage />
                  </Suspense>
                }
              />
              <Route
                path="/projects/clients/:clientId"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <ClientDetailPage />
                  </Suspense>
                }
              />
              <Route
                path="/projects/list"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <ProjectsPage />
                  </Suspense>
                }
              />
              <Route
                path="/projects/sidur"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <SidurPage />
                  </Suspense>
                }
              />
              <Route
                path="/projects/daily-reports"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <AdminDailyReportsPage />
                  </Suspense>
                }
              />
              <Route
                path="/projects/toolbox"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <ToolboxPage />
                  </Suspense>
                }
              />
              <Route
                path="/projects/onboard/:dealId"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <DealOnboardingPage />
                  </Suspense>
                }
              />
              <Route
                path="/projects/:projectId"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <ProjectDetailPage />
                  </Suspense>
                }
              />

              {/* RECHESH SECTION */}
              <Route
                path="/rechesh/orders/new"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <OrderNewPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/orders"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <OrdersPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/suppliers"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <SuppliersPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/suppliers/:supplierId"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <SupplierDetailPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/supplies"
                element={<Navigate to="/rechesh/inventory" replace />}
              />
              <Route
                path="/rechesh/inventory"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <StockListPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/inventory/:itemId"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <ItemDetailPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/warehouse"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <WarehouseIncomingPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/warehouse/printer-diag"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <PrinterDiagnosticsPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/warehouse/returns"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <ReturnsConfirmPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/movements"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <MovementsPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/goods-receiving"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <GoodsReceivingPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/purchase-requests"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <PurchaseRequestsPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/supplier-bids"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <SupplierBidsPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/supplier-bids/:bidId"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <SupplierBidRequestDetailPage />
                  </Suspense>
                }
              />
              <Route
                path="/rechesh/supplier-bids/:bidId/suppliers/:supplierId/entry"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <SupplierBidEntryPage />
                  </Suspense>
                }
              />

              {/* ADMIN SECTION - restricted to admin users only */}
              <Route
                path="/admin/vacation-requests"
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoadingFallback />}>
                      <AdminVacationRequestsPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/petty-cash"
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoadingFallback />}>
                      <AdminPettyCashPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/payslips"
                element={
                  <SuperAdminRoute>
                    <Suspense fallback={<PageLoadingFallback />}>
                      <AdminPayslipsPage />
                    </Suspense>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/time-reports"
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoadingFallback />}>
                      <AdminTimeReportsPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/daily-reports"
                element={<Navigate to="/projects/daily-reports" replace />}
              />
              <Route
                path="/admin/notifications"
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoadingFallback />}>
                      <AdminNotificationsPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/reminder-settings"
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoadingFallback />}>
                      <AdminReminderSettingsPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/holidays"
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoadingFallback />}>
                      <AdminHolidaysPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/audit-logs"
                element={
                  <SuperAdminRoute>
                    <Suspense fallback={<PageLoadingFallback />}>
                      <AdminAuditLogsPage />
                    </Suspense>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/gmail-auth/callback"
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoadingFallback />}>
                      <GmailAuthCallbackPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/workers"
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoadingFallback />}>
                      <WorkersPage />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/workers/:workerId"
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoadingFallback />}>
                      <WorkerDetailPage />
                    </Suspense>
                  </AdminRoute>
                }
              />

              {/* Legacy redirects */}
              <Route path="/clients" element={<Navigate to="/projects/clients" replace />} />
              <Route path="/projects" element={<Navigate to="/projects/list" replace />} />
              <Route path="/sidur" element={<Navigate to="/projects/sidur" replace />} />
              <Route path="/orders" element={<Navigate to="/rechesh/orders" replace />} />
              <Route path="/suppliers" element={<Navigate to="/rechesh/suppliers" replace />} />
              <Route path="/supplies" element={<Navigate to="/rechesh/inventory" replace />} />
              <Route
                path="/goods-receiving"
                element={<Navigate to="/rechesh/goods-receiving" replace />}
              />
              <Route path="*" element={<Navigate to="/projects/clients" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export function App() {
  return <AppContent />;
}
